import { formatNumber } from 'utils/numberFormat';
import { HTMLAttributes, memo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { DownOutlined } from '@ant-design/icons';
import Fold from './Fold';
import { DetailItem, Amount, Detail, priceRender } from './TradeDetailPaymentLazada';
import { Divider, Spin } from 'antd';
import { LIHeaderChild } from './LIHeaderChild';
import { cx } from '@emotion/css';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { useRequest } from 'ahooks';
import { overseaTranslate } from 'services/api/overseaTranslate';
import useSystem from 'hooks/business/system/useSystem';

export interface TradeIncomeAmountProps extends HTMLAttributes<HTMLElement> {
  trade: Conversation.Trade.Vo;
  defaultFold?: boolean;
}

/** shopee 买家实付金额 */
function TradeDetailIncomeShopee(props: TradeIncomeAmountProps) {
  const { trade, defaultFold = true, className, ...otherProps } = props;

  const { unit, tradeIncome } = trade;

  const { t } = useTranslate();

  const [isfold, setIsfold] = useState(defaultFold);

  const isTradeDetail = className === 'tradeDetail';

  const totalIncome = tradeIncome?.finalAmount || tradeIncome?.escrowAmount;
  const total = priceRender(totalIncome, (val) => {
    return (
      <DetailItem
        key="total"
        onClick={() => setIsfold((isfold) => !isfold)}
        className={
          isTradeDetail
            ? 'border border-solid border-[#F0F2F5] px-4 py-0 leading-[40px]'
            : undefined
        }
      >
        <span className={isTradeDetail ? 'font-bold' : undefined}>
          {t('domain.order.income.field.orderIncome')}
        </span>
        {!isTradeDetail ? (
          <Amount isfold={isfold}>
            <div>{`${unit} ${formatNumber(val, '0,0.00')}`}</div>
            <DownOutlined />
          </Amount>
        ) : null}
      </DetailItem>
    );
  });

  const detail = (
    <Detail
      key="detail"
      className={cx([
        '!px-0',
        isTradeDetail
          ? '!bg-fill-0 !px-0 border-t-0 border border-solid border-[#F0F2F5]'
          : undefined,
      ])}
    >
      <div className="px-4">
        {priceRender(tradeIncome?.itemPrice, (val) => {
          return (
            <li key="itemPrice">
              <dt>{t('domain.order.income.field.productAmount')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </li>
          );
        })}
        {priceRender(tradeIncome?.originItemPrice, (val) => {
          return (
            <LIHeaderChild key="originItemPrice">
              <dt>{t('domain.order.income.field.productPrice')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.refundItemPrice, (val) => {
          return (
            <LIHeaderChild key="refundItemPrice">
              <dt>{t('domain.order.income.field.refundAmount')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.shippingFee, (val) => {
          return (
            <li key="shippingFee">
              <dt>{t('domain.order.income.field.shippingSubtotal')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </li>
          );
        })}
        {priceRender(tradeIncome?.buyerPaidShippingFee, (val) => {
          return (
            <LIHeaderChild key="buyerPaidShippingFee">
              <dt>{t('domain.order.income.field.buyerPaidShipping')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.actualShippingFee, (val) => {
          return (
            <LIHeaderChild key="actualShippingFee">
              <dt>{t('domain.order.income.field.logisticsShippingFee')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.refundShippingFee, (val) => {
          return (
            <LIHeaderChild key="refundShippingFee">
              <dt>{t('domain.order.income.field.refundShippingFee')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.shopeeShippingRebate, (val) => {
          return (
            <LIHeaderChild key="shopeeShippingRebate">
              <dt>{t('domain.order.income.field.shopeeShippingSubsidy')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.shippingFeeDiscountFrom3pl, (val) => {
          return (
            <LIHeaderChild key="shippingFeeDiscountFrom3pl">
              <dt>{t('domain.order.income.field.shippingDiscount')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.discountFee, (val) => {
          return (
            <li key="discountFee">
              <dt>{t('domain.order.income.field.vouchersAndSubsidies')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </li>
          );
        })}
        {priceRender(tradeIncome?.voucherFromSeller, (val) => {
          return (
            <LIHeaderChild key="voucherFromSeller">
              <dt>{t('domain.order.income.field.sellerVoucher')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.shopeeDiscount, (val) => {
          return (
            <LIHeaderChild key="shopeeDiscount">
              <dt>{t('domain.order.income.field.shopeeProductSubsidy')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.chargesFee, (val) => {
          return (
            <li key="chargesFee">
              <dt>{t('domain.order.income.field.feesAndCharges')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </li>
          );
        })}
        {priceRender(tradeIncome?.commissionFee, (val) => {
          return (
            <LIHeaderChild key="commissionFee">
              <dt>{t('domain.order.income.field.commission')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.serviceFee, (val) => {
          return (
            <LIHeaderChild key="serviceFee">
              <dt>{t('domain.order.income.field.serviceFee')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.sellerTransactionFee, (val) => {
          return (
            <LIHeaderChild key="sellerTransactionFee">
              <dt>{t('domain.order.income.field.transactionFee')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.vat, (val) => {
          return (
            <LIHeaderChild>
              <dt>{t('domain.order.income.field.valueAddedTax')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {priceRender(tradeIncome?.crossBorderTax, (val) => {
          return (
            <LIHeaderChild>
              <dt>{t('domain.order.income.field.importDuty')}</dt>
              <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </LIHeaderChild>
          );
        })}
        {['MY', 'SG'].includes(trade?.region) &&
          priceRender(tradeIncome?.salesTaxOnLvg, (val) => {
            return (
              <LIHeaderChild key="salesTaxOnLvg">
                <dt>{`${t('domain.order.income.field.lowValueGoodsTax')}`}</dt>
                <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
              </LIHeaderChild>
            );
          })}
        {priceRender(tradeIncome?.escrowAmount, (val) => {
          return (
            <li key="escrowAmount">
              <dt>{t('domain.order.income.field.orderReceivable')}</dt>
              <dd className="colorFont">{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
            </li>
          );
        })}
      </div>

      {tradeIncome?.tradeAdjustment?.length > 0 ? (
        <>
          <Divider className="!my-2" />
          <div className="px-4">
            <AdjustmentReason unit={unit} tradeAdjustment={tradeIncome.tradeAdjustment} />
            {priceRender(tradeIncome?.totalAdjustment, (val) => {
              return (
                <>
                  <li key="totalAdjustment">
                    <dt>{t('domain.order.income.field.adjustmentTotal')}</dt>
                    <dd className="colorFont">{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
                  </li>
                </>
              );
            })}
          </div>
        </>
      ) : null}

      {tradeIncome?.finalAmount ? (
        <>
          <Divider className="!my-2" />
          <div className="px-4">
            {priceRender(tradeIncome.finalAmount, (val) => {
              return (
                <li key="finalAmount">
                  <dt>{t('domain.order.income.field.finalAmount')}</dt>
                  <dd className="colorFont">{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
                </li>
              );
            })}
          </div>
        </>
      ) : null}
    </Detail>
  );

  return total ? (
    <Fold
      isfold={isfold}
      fixedCount={1}
      nodeList={[total, detail]}
      defaultFixedHeight={32}
      {...otherProps}
      className={isTradeDetail ? 'mb-4' : undefined}
    />
  ) : null;
}

export default memo(TradeDetailIncomeShopee);

export const AdjustmentReason = (props: {
  unit: string;
  tradeAdjustment: Conversation.Trade.TradeAdjustment[];
}) => {
  const { unit, tradeAdjustment } = props;
  const {
    systemSetting: { language: sysLang },
  } = useSystem();

  const { loading, data } = useRequest(async () => {
    const texts = tradeAdjustment.map(({ adjustmentReason }) => adjustmentReason);
    const translates = await overseaTranslate(texts, sysLang, 'side');
    return tradeAdjustment.map((data, index) => {
      return {
        ...data,
        translate: translates[index],
      };
    });
  });

  return loading ? (
    <Spin spinning />
  ) : (
    <>
      {data?.map(({ translate, adjustmentReason, adjustmentTimeMs, amount }) => {
        const validTime = !!adjustmentTimeMs && adjustmentTimeMs !== '0';
        return (
          <LIHeaderChild>
            <dt className="overflow-hidden text-ellipsis  line-clamp-2 leading-snug">
              {validTime && (
                <span className="mr-1">
                  {dayjs(toNumber(adjustmentTimeMs)).format('YYYY-MM-DD')}
                </span>
              )}
              <span>{translate || adjustmentReason}</span>
            </dt>
            <dd>{`${unit} ${formatNumber(amount, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
    </>
  );
};
